import React, { useState, useEffect } from 'react';
import {
  LineChart, 
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  Cell
} from 'recharts';
import { 
  format as formatDate,
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  eachWeekOfInterval, 
  eachMonthOfInterval, 
  startOfDay, 
  endOfDay,
  differenceInDays
} from 'date-fns';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Button,
  TablePagination,
  Divider,
  IconButton
} from '@mui/material';
import {
  AccessTime,
  Person,
  Repeat,
  Timer,
  Search,
  CalendarToday,
  InsertDriveFile,
  FilterList,
  NoPhotography
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

const API_BASE_URL = 'https://gnrl-bknd-server.onrender.com/api';

const Analytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dateRange, setDateRange] = useState('daily');
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({
    dailyLogins: [],
    completedFilesByFolder: [], // Data for completed files by folder
    completedFilesCount: 0, // Total count of completed files
    completedFilesByDate: [], // Added for completed files by date
    practiceTimeByFolder: [] // Add new state for practice time data
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folderStructure, setFolderStructure] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [fileRepeats, setFileRepeats] = useState([]);
  const [isLoadingFileRepeats, setIsLoadingFileRepeats] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState('engagement');
  const [selectedDate, setSelectedDate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [searchTerm, setSearchTerm] = useState('');

  // Custom colors for the bar chart
  const FOLDER_COLORS = {
    total: '#EF8354',  // Orange color for total files
    completed: '#4F86C6'  // Blue color for completed files
  };

  // Fetch users for the dropdown
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users list');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    const today = new Date();
    
    switch (newRange) {
      case 'daily':
        setStartDate(subDays(today, 7));
        setEndDate(today);
        break;
      case 'weekly':
        setStartDate(subDays(today, 30));
        setEndDate(today);
        break;
      case 'monthly':
        setStartDate(subDays(today, 90));
        setEndDate(today);
        break;
      default:
        break;
    }
  };

  const getTickInterval = (data) => {
    if (!data || data.length === 0) return 0;
    const dataLength = data.length;
    
    // Adjust interval based on data length
    if (dataLength <= 7) return 0;        // Show all points for a week or less
    if (dataLength <= 14) return 1;       // Show every other day for two weeks
    if (dataLength <= 31) return 2;       // Show every third day for a month
    if (dataLength <= 90) return 6;       // Show weekly for three months
    return Math.floor(dataLength / 12);   // Show roughly 12 points for longer periods
  };

  const formatXAxisDate = (dateStr, dateRange) => {
    const date = new Date(dateStr);
    switch (dateRange) {
      case 'daily':
        return formatDate(date, 'MMM d');
      case 'weekly':
        return formatDate(date, 'MMM d');
      case 'monthly':
        return formatDate(date, 'MMM yyyy');
      default:
        return dateStr;
    }
  };

  const formatYAxisTick = (value) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value;
  };

  const processDataByDateRange = (data, start, end, rangeType) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    // Ensure endDate includes the entire current day
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);
    
    const daysDifference = differenceInDays(adjustedEndDate, startDate);

    let intervals = [];
    let dateFormat = 'MMM d';

    switch (rangeType) {
      case 'daily':
        intervals = eachDayOfInterval({ start: startDate, end: adjustedEndDate });
        dateFormat = daysDifference > 30 ? 'MMM d' : 'MMM d';
        break;
      case 'weekly':
        intervals = eachWeekOfInterval({ start: startDate, end: adjustedEndDate });
        dateFormat = daysDifference > 90 ? 'MMM d' : "'Week of' MMM d";
        break;
      case 'monthly':
        intervals = eachMonthOfInterval({ start: startDate, end: adjustedEndDate });
        dateFormat = 'MMM yyyy';
        break;
      default:
        intervals = eachDayOfInterval({ start: startDate, end: adjustedEndDate });
    }

    const processedData = intervals.map(date => ({
      date: formatDate(date, dateFormat),
      count: 0,
      tooltipDate: formatDate(date, 'MMM d, yyyy')
    }));

    data.forEach(item => {
      try {
        const itemDate = new Date(item.date);
        let intervalIndex = -1;

        switch (rangeType) {
          case 'daily':
            intervalIndex = intervals.findIndex(date => 
              startOfDay(date).getTime() === startOfDay(itemDate).getTime()
            );
            break;
          case 'weekly':
            intervalIndex = intervals.findIndex(date =>
              itemDate >= startOfWeek(date) && itemDate < endOfWeek(date)
            );
            break;
          case 'monthly':
            intervalIndex = intervals.findIndex(date =>
              itemDate >= startOfMonth(date) && itemDate < endOfMonth(date)
            );
            break;
        }

        if (intervalIndex !== -1) {
          processedData[intervalIndex].count += item.count;
        }
      } catch (err) {
        console.error('Error processing data item:', item, err);
      }
    });

    return processedData;
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Set end date to include the full day
      const endOfCurrentDay = new Date(endDate);
      endOfCurrentDay.setHours(23, 59, 59, 999);
      
      // Format dates properly for API calls
      const formattedStartDate = formatDate(new Date(startDate), 'yyyy-MM-dd');
      const formattedEndDate = formatDate(endOfCurrentDay, 'yyyy-MM-dd');
      const userParam = selectedUser ? `&username=${selectedUser.username}` : '';
      
      console.log('Fetching analytics with date range:', { 
        startDate: formattedStartDate, 
        endDate: formattedEndDate,
        user: selectedUser?.username || 'all' 
      });
      
      // First fetch all users to ensure we have data for every user
      const usersResponse = await axios.get(`${API_BASE_URL}/users`);
      
      const [dailyLoginsRes, completedFilesByFolderRes, completedFilesCountRes, completedFilesByDateRes, practiceTimeRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/analytics/daily-logins?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/completed-files-by-folder?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/completed-files-count?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/completed-files-by-date?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/practice-time-by-folder?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`)
      ]);

      console.log('Daily logins API response:', dailyLoginsRes.data);
      console.log('Completed files by folder API response:', completedFilesByFolderRes.data);
      console.log('Completed files count API response:', completedFilesCountRes.data);

      const processedDailyLogins = processDataByDateRange(
        dailyLoginsRes.data,
        startDate,
        endOfCurrentDay,
        dateRange
      );

      // Process the folder data to create shorter display names
      const processedFolderData = completedFilesByFolderRes.data.map(folder => {
        // Get just the folder name without full path for display
        const displayName = folder.name.split('/').pop() || folder.name;
        return {
          ...folder,
          displayName
        };
      });
      
      console.log('Completed files by date API response:', completedFilesByDateRes.data);
      
      // Process the completed files data
      const processedCompletedFiles = completedFilesByDateRes.data.map(item => {
        console.log('Processing completed files item:', item);
        return {
          ...item,
          date: new Date(item.date).toISOString().split('T')[0],
          count: item.count,
          files: Array.isArray(item.files) ? item.files.map(file => ({
            ...file,
            completedAt: new Date(file.completedAt).toISOString()
          })) : []
        };
      });
      
      console.log('Processed completed files:', processedCompletedFiles);

      setAnalyticsData({
        dailyLogins: processedDailyLogins,
        completedFilesByFolder: processedFolderData,
        completedFilesCount: completedFilesCountRes.data.count,
        completedFilesByDate: processedCompletedFiles,
        practiceTimeByFolder: practiceTimeRes.data
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [startDate, endDate, selectedUser, dateRange]);

  // Add to useEffect for initial load
  useEffect(() => {
    const fetchFolderStructure = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/analytics/folder-structure`);
        console.log('Folder structure:', response.data);
        setFolderStructure(response.data);
      } catch (error) {
        console.error('Error fetching folder structure:', error);
        setError('Failed to fetch folder structure');
      }
    };

    fetchFolderStructure();
  }, []);

  // Add function to fetch file repeats for selected folder
  const fetchFileRepeats = async (folderPath) => {
    try {
      setIsLoadingFileRepeats(true);
      console.log('Fetching file repeats for folder:', folderPath);
      
      const response = await axios.get(`${API_BASE_URL}/analytics/file-repeats`, {
        params: {
          startDate: formatDate(startDate, 'yyyy-MM-dd'),
          endDate: formatDate(endDate, 'yyyy-MM-dd'),
          username: selectedUser?.username,
          folderPath
        }
      });
      
      console.log('File repeats response:', response.data);
      setFileRepeats(response.data);
    } catch (error) {
      console.error('Error fetching file repeats:', error);
      setError('Failed to fetch file repeats data');
    } finally {
      setIsLoadingFileRepeats(false);
    }
  };

  // Add handler for folder selection
  const handleFolderSelect = (event) => {
    const folderPath = event.target.value;
    console.log('Selected folder:', folderPath);
    setSelectedFolder(folderPath);
    fetchFileRepeats(folderPath);
  };

  // Recursive function to render folder options
  const renderFolderOptions = (folder, level = 0) => {
    const indent = '  '.repeat(level);
    return [
      <MenuItem key={folder.path} value={folder.path}>
        {indent + folder.name}
      </MenuItem>,
      ...(folder.subfolders || []).flatMap(sf => renderFolderOptions(sf, level + 1))
    ];
  };

  // Update the file card component
  const FileCard = ({ file }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <InsertDriveFile sx={{ 
          color: '#4F86C6',
          fontSize: '1.25rem',
          mt: 0.25
        }} />
        <Typography 
          sx={{ 
            fontWeight: 500,
            color: '#2d3748',
            fontSize: '0.95rem',
            wordBreak: 'break-word',
            flex: 1
          }}
        >
          {file.fileName}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime sx={{ 
              color: '#718096',
              fontSize: '1.1rem'
            }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Completed at
              </Typography>
              <Typography variant="body1">
                {formatDate(new Date(file.completedAt), 'h:mm a')}
              </Typography>
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ 
              color: '#718096',
              fontSize: '1.1rem'
            }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                User
              </Typography>
              <Typography variant="body1" color="primary">
                {file.username}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mt: 2
      }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          px: 2,
          py: 1,
          flex: 1
        }}>
          <Repeat sx={{ 
            color: '#718096',
            fontSize: '1.1rem'
          }} />
          <Box>
            <Typography variant="body2" color="text.secondary">
              Repeats
            </Typography>
            <Typography variant="body1">
              {file.repeats}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          px: 2,
          py: 1,
          flex: 1
        }}>
          <Timer sx={{ 
            color: '#718096',
            fontSize: '1.1rem'
          }} />
          <Box>
            <Typography variant="body2" color="text.secondary">
              Duration
            </Typography>
            <Typography variant="body1">
              {file.playTime}s
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  const renderSelectedGraph = () => {
    switch (selectedGraph) {
      case 'engagement':
        return (
          <Card sx={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
            }
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                {selectedUser ? `${selectedUser.username}'s Engagement Over Time` : 'Student Engagement Over Time'}
              </Typography>
              {analyticsData.dailyLogins.length > 0 ? (
                <Box sx={{ overflowX: isMobile ? 'auto' : 'visible', background: isMobile ? '#f4f6fa' : 'inherit', borderRadius: 2, p: isMobile ? 1 : 0, mb: 2 }}>
                  <ResponsiveContainer width={isMobile ? 700 : '100%'} height={400}>
                    <AreaChart 
                      data={analyticsData.dailyLogins}
                      margin={{ top: 20, right: 30, left: 40, bottom: 90 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                      <XAxis 
                        dataKey="date"
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        interval={isMobile ? 'preserveStartEnd' : 0}
                        tick={{ 
                          fontSize: 12,
                          fill: '#666666'
                        }}
                        tickFormatter={(value) => formatXAxisDate(value, dateRange)}
                        padding={{ left: 20, right: 20 }}
                        tickMargin={25}
                      />
                      <YAxis 
                        tickFormatter={(value) => {
                          if (value >= 1000) return `${(value/1000).toFixed(1)}k`;
                          return value;
                        }}
                        tick={{ 
                          fontSize: isMobile ? 10 : 12,
                          fill: '#666666'
                        }}
                        width={60}
                        padding={{ top: 20, bottom: 20 }}
                        tickMargin={10}
                        domain={[0, 'auto']}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value} activities`, 'Student Activities']}
                        labelFormatter={(label) => {
                          const dataPoint = analyticsData.dailyLogins.find(d => d.date === label);
                          return dataPoint ? dataPoint.tooltipDate : label;
                        }}
                        contentStyle={{ 
                          fontSize: '12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #cccccc',
                          borderRadius: '8px',
                          padding: '10px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ 
                          paddingTop: 20,
                          fontSize: isMobile ? 10 : 12
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#5e35b1"
                        fill="url(#colorGradient)"
                        name="Student Activities"
                        strokeWidth={2}
                      />
                      <defs>
                        <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#673ab7" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#9575cd" stopOpacity={0.2}/>
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 10 }}>
                  No engagement data available for the selected date range
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      case 'filesByFolder':
        return (
          <Card sx={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
            }
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                Audio Files by Folder
              </Typography>
              {analyticsData.completedFilesByFolder && analyticsData.completedFilesByFolder.length > 0 ? (
                <Box sx={{ 
                  height: '400px',
                  overflowX: isMobile ? 'auto' : 'visible',
                  overflowY: 'hidden',
                  background: isMobile ? '#f4f6fa' : 'inherit',
                  borderRadius: 2,
                  p: isMobile ? 1 : 0,
                  mb: 2
                }}>
                  <ResponsiveContainer width={isMobile ? 700 : '100%'} height="100%">
                    <BarChart 
                      data={analyticsData.completedFilesByFolder}
                      margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                      <XAxis 
                        dataKey="displayName"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                        tick={{ 
                          fontSize: 12,
                          fill: '#666666'
                        }}
                        tickMargin={25}
                      />
                      <YAxis 
                        tickFormatter={(value) => `${value} files`}
                        tick={{ 
                          fontSize: 12,
                          fill: '#666666'
                        }}
                        width={60}
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'total') return [`${value} files`, 'Total Files'];
                          if (name === 'completed') return [`${value} files`, 'Completed Files'];
                          return [value, name];
                        }}
                        labelFormatter={(value) => `Folder: ${value}`}
                        contentStyle={{ 
                          fontSize: '12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #cccccc',
                          borderRadius: '8px',
                          padding: '10px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ 
                          paddingTop: 20,
                          fontSize: 12
                        }}
                      />
                      <Bar 
                        dataKey="total" 
                        name="Total Files" 
                        fill={FOLDER_COLORS.total}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        dataKey="completed" 
                        name="Completed Files" 
                        fill={FOLDER_COLORS.completed}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 10 }}>
                  No audio files found for the selected date range
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      case 'filesByDate':
        return (
          <Card sx={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
            }
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                {selectedUser 
                  ? `${selectedUser.username}'s Completed Files by Date` 
                  : 'Completed Files by Date'}
              </Typography>
              {analyticsData.completedFilesByDate && analyticsData.completedFilesByDate.length > 0 ? (
                <>
                  <Box sx={{ overflowX: isMobile ? 'auto' : 'visible', background: isMobile ? '#f4f6fa' : 'inherit', borderRadius: 2, p: isMobile ? 1 : 0, mb: 2 }}>
                    <ResponsiveContainer width={isMobile ? 700 : '100%'} height={400}>
                      <BarChart 
                        data={analyticsData.completedFilesByDate}
                        margin={{ top: 20, right: 30, left: 40, bottom: 90 }}
                        onClick={(data) => {
                          if (data && data.activePayload && data.activePayload.length > 0) {
                            console.log('Click event data:', {
                              clickedDate: data.activeLabel,
                              payload: data.activePayload[0],
                              rawData: analyticsData.completedFilesByDate
                            });
                            
                            const clickedDate = data.activeLabel;
                            const clickedData = analyticsData.completedFilesByDate.find(d => {
                              console.log('Comparing dates:', {
                                clickedDate,
                                itemDate: d.date,
                                match: d.date === clickedDate,
                                itemData: d
                              });
                              return d.date === clickedDate;
                            });
                            
                            if (clickedData) {
                              console.log('Found clicked data:', {
                                date: clickedData.date,
                                count: clickedData.count,
                                filesLength: clickedData.files?.length,
                                files: clickedData.files
                              });
                              
                              setSelectedDate(clickedData);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis 
                          dataKey="date"
                          angle={-45}
                          textAnchor="end"
                          height={90}
                          interval={isMobile ? 'preserveStartEnd' : 0}
                          tick={{ 
                            fontSize: 12,
                            fill: '#666666'
                          }}
                          tickFormatter={(value) => formatXAxisDate(value, dateRange)}
                          padding={{ left: 20, right: 20 }}
                          tickMargin={25}
                        />
                        <YAxis 
                          tickFormatter={formatYAxisTick}
                          tick={{ 
                            fontSize: isMobile ? 10 : 12,
                            fill: '#666666' 
                          }}
                          tickMargin={10}
                        />
                        <Tooltip 
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const formattedLabel = new Date(label).toISOString().split('T')[0];
                              const dateData = analyticsData.completedFilesByDate.find(d => {
                                const formattedDate = new Date(d.date).toISOString().split('T')[0];
                                return formattedDate === formattedLabel;
                              });
                              
                              if (!dateData) return null;
                              
                              return (
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                  padding: '12px',
                                  border: '1px solid #cccccc',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  fontSize: '13px',
                                  maxWidth: '300px',
                                  maxHeight: '400px',
                                  overflowY: 'auto'
                                }}>
                                  <p style={{ 
                                    margin: '0 0 8px 0', 
                                    fontWeight: 'bold',
                                    color: '#2d3748'
                                  }}>
                                    {formatDate(new Date(dateData.date), 'MMMM d, yyyy')}
                                  </p>
                                  <p style={{ 
                                    margin: '0 0 8px 0',
                                    color: '#4F86C6',
                                    fontWeight: '600'
                                  }}>
                                    {dateData.count} Files Completed
                                  </p>
                                  {dateData.files && dateData.files.length > 0 && (
                                    <div style={{
                                      marginTop: '8px',
                                      borderTop: '1px solid #e2e8f0',
                                      paddingTop: '8px'
                                    }}>
                                      <p style={{
                                        margin: '0 0 4px 0',
                                        fontSize: '12px',
                                        color: '#718096'
                                      }}>
                                        
                                      </p>
                                      {/* {dateData.files.map((file, index) => (
                                        <div key={index} style={{
                                          fontSize: '12px',
                                          marginBottom: '4px',
                                          color: '#4a5568'
                                        }}>
                                          • {file.fileName}
                                        </div>
                                      ))} */}
                                    </div>
                                  )}
                                  <p style={{
                                    margin: '8px 0 0 0',
                                    color: '#718096',
                                    fontSize: '12px',
                                    fontStyle: 'italic',
                                    borderTop: '1px solid #e2e8f0',
                                    paddingTop: '8px'
                                  }}>
                                    Click to view details
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar 
                          dataKey="count" 
                          name="Completed Files" 
                          fill="#4F86C6"
                          radius={[4, 4, 0, 0]}
                          cursor="pointer"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                  
                  {selectedDate && (
                    <Box 
                      sx={{ 
                        mt: 3,
                        p: 3,
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CalendarToday sx={{ color: '#4F86C6' }} />
                          <Typography variant="h6" sx={{ color: '#2d3748' }}>
                            Files Completed on {formatDate(new Date(selectedDate.date), 'MMMM d, yyyy')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search files or users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <Search sx={{ color: '#718096', mr: 1 }} />
                              ),
                            }}
                            sx={{ 
                              minWidth: 250,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white'
                              }
                            }}
                          />
                          <IconButton 
                            size="small"
                            sx={{ 
                              backgroundColor: 'white',
                              border: '1px solid #e2e8f0',
                              '&:hover': {
                                backgroundColor: '#f8fafc'
                              }
                            }}
                          >
                            <FilterList />
                          </IconButton>
                        </Box>
                      </Box>

                      {selectedDate.files && selectedDate.files.length > 0 ? (
                        <>
                          <Grid container spacing={2}>
                            {filterFiles(selectedDate.files)
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((file, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                  <FileCard file={file} />
                                </Grid>
                            ))}
                          </Grid>

                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            mt: 3,
                            pt: 2,
                            borderTop: '1px solid #e2e8f0'
                          }}>
                            <TablePagination
                              component="div"
                              count={filterFiles(selectedDate.files).length}
                              page={page}
                              onPageChange={handleChangePage}
                              rowsPerPage={rowsPerPage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              rowsPerPageOptions={[9, 18, 36, 72]}
                              sx={{
                                '.MuiTablePagination-select': {
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          </Box>
                        </>
                      ) : (
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            py: 8,
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <NoPhotography sx={{ 
                            color: '#A0AEC0', 
                            fontSize: '48px',
                            mb: 2,
                            opacity: 0.5
                          }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Files Found
                          </Typography>
                          <Typography color="text.secondary">
                            No file details available for this date.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 10 }}>
                  No completed files found for the selected date range
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      case 'fileRepeats':
        return (
          <Card sx={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
            }
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                {selectedUser 
                  ? `${selectedUser.username}'s File Repeats by Folder` 
                  : 'File Repeats by Folder'}
              </Typography>
              {analyticsData.practiceTimeByFolder && analyticsData.practiceTimeByFolder.length > 0 ? (
                <Box sx={{ overflowX: isMobile ? 'auto' : 'visible', background: isMobile ? '#f4f6fa' : 'inherit', borderRadius: 2, p: isMobile ? 1 : 0, mb: 2 }}>
                  <ResponsiveContainer width={isMobile ? 700 : '100%'} height={isMobile ? Math.max(400, analyticsData.practiceTimeByFolder.length * 60) : 500}>
                    <BarChart 
                      data={analyticsData.practiceTimeByFolder}
                      margin={isMobile ? 
                        { top: 20, right: 15, left: 15, bottom: 20 } : 
                        { top: 20, right: 30, left: 150, bottom: 20 }
                      }
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.6} horizontal={true} vertical={false} />
                      <XAxis 
                        type="number"
                        tickFormatter={(value) => `${value} times`}
                        tick={{ 
                          fontSize: isMobile ? 10 : 12,
                          fill: '#666666'
                        }}
                      />
                      <YAxis 
                        dataKey="name"
                        type="category"
                        width={isMobile ? 100 : 140}
                        tick={{ 
                          fontSize: isMobile ? 10 : 12,
                          fill: '#666666' 
                        }}
                        tickFormatter={isMobile ? (value) => {
                          const parts = value.split('/');
                          return parts[parts.length - 1].length > 12 ? 
                            parts[parts.length - 1].substring(0, 10) + '...' : 
                            parts[parts.length - 1];
                        } : undefined}
                        tickMargin={10}
                      />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const folder = analyticsData.practiceTimeByFolder.find(f => f.name === label);
                            return (
                              <div style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                padding: '10px',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                fontSize: isMobile ? '11px' : '12px',
                                maxWidth: isMobile ? '200px' : '300px'
                              }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                                  {folder.name}
                                </p>
                                <p style={{ margin: '0 0 5px 0' }}>
                                  {`Total Repeats: ${folder.totalRepeats}`}
                                </p>
                                <div style={{ 
                                  maxHeight: isMobile ? '150px' : '200px', 
                                  overflowY: 'auto',
                                  fontSize: isMobile ? '10px' : '12px',
                                  paddingRight: '8px'
                                }}>
                                  <p style={{ margin: '5px 0', fontWeight: 'bold' }}>Files:</p>
                                  {folder.files.map((file, index) => (
                                    <p key={index} style={{ margin: '2px 0' }}>
                                      {`${file.name}: ${file.repeats} times`}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar 
                        dataKey="totalRepeats" 
                        name="Total Repeats" 
                        fill="#8884d8"
                        radius={[0, 4, 4, 0]}
                      >
                        {analyticsData.practiceTimeByFolder.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`}
                            fill={`hsl(${(index * 25) % 360}, 70%, 60%)`}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 10 }}>
                  No repeat data found for the selected date range
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filterFiles = (files) => {
    if (!searchTerm) return files;
    return files.filter(file => 
      file.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <Box sx={{ 
      p: 4, 
      backgroundColor: '#f5f7fa',
      minHeight: '100vh'
    }}>
      <Typography variant="h4" gutterBottom sx={{ 
        fontWeight: 600, 
        color: '#1a237e',
        mb: 3,
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: 1
      }}>
        Student Analytics Dashboard
      </Typography>

      {/* Filters Section */}
      <Box sx={{ 
        mb: 4, 
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {/* Other Filters */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="date-range-label">Time Period</InputLabel>
                <Select
                  labelId="date-range-label"
                  value={dateRange}
                  label="Time Period"
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="daily">Daily (Last 7 days)</MenuItem>
                  <MenuItem value="weekly">Weekly (Last 30 days)</MenuItem>
                  <MenuItem value="monthly">Monthly (Last 90 days)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      variant: "outlined",
                      sx: { borderRadius: '8px' }
                    } 
                  }}
                  maxDate={endDate}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      variant: "outlined",
                      sx: { borderRadius: '8px' }
                    } 
                  }}
                  minDate={startDate}
                  maxDate={new Date()}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                value={selectedUser}
                onChange={(event, newValue) => setSelectedUser(newValue)}
                options={users}
                getOptionLabel={(option) => option.username || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Student"
                    fullWidth
                    variant="outlined"
                    sx={{ borderRadius: '8px' }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#1a237e' }} />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {/* Total Completed Files Count Card */}
          <Grid item xs={12}>
            <Card sx={{ 
              borderRadius: '12px', 
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                py: 4
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                  {selectedUser 
                    ? `${selectedUser.username}'s Total Completed Audio Files` 
                    : 'Total Completed Audio Files'}
                </Typography>
                
                <Typography variant="h2" sx={{ 
                  fontWeight: 700, 
                  color: '#5e35b1',
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {analyticsData.completedFilesCount}
                  <Typography variant="body1" component="span" sx={{ ml: 1, color: '#666', fontSize: '1rem' }}>
                    files
                  </Typography>
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                  For the period: {formatDate(startDate, 'MMM d, yyyy')} - {formatDate(endDate, 'MMM d, yyyy')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Graph Selector Tabs */}
          <Grid item xs={12}>
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              <Box sx={{
                display: 'flex',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                backgroundColor: '#f8fafc',
                position: 'relative'
              }}>
                {[
                  { id: 'engagement', label: 'Activity' },
                  { id: 'filesByFolder', label: 'Files by Folder' },
                  { id: 'filesByDate', label: 'Files by Date' },
                  { id: 'fileRepeats', label: 'Repeats by Folder' }
                ].map((tab) => (
                  <Box
                    key={tab.id}
                    onClick={() => setSelectedGraph(tab.id)}
                    sx={{
                      flex: 1,
                      py: 2,
                      px: 3,
                      cursor: 'pointer',
                      textAlign: 'center',
                      position: 'relative',
                      color: selectedGraph === tab.id ? 'primary.main' : 'text.secondary',
                      fontWeight: selectedGraph === tab.id ? 600 : 400,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: selectedGraph === tab.id ? 'primary.main' : 'text.primary',
                        backgroundColor: 'rgba(0, 0, 0, 0.01)'
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '2px',
                        backgroundColor: 'primary.main',
                        transform: selectedGraph === tab.id ? 'scaleX(1)' : 'scaleX(0)',
                        transition: 'transform 0.3s ease'
                      }
                    }}
                  >
                    <Typography variant="subtitle1">
                      {tab.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Selected Graph */}
          <Grid item xs={12}>
            {renderSelectedGraph()}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Analytics; 
